import { useCallback, useRef, useState, useEffect } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useDaywiseByStationwiseGroup(range = null, stationIds = null) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const { send, isConnected, connect, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const retryTimeoutRef = useRef(null);

  const groupId = localStorage.getItem("group_id");
  const roleId = localStorage.getItem("role_id");

  const defaultRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);
    const format = (d) => d.toISOString().split("T")[0];
    return { start_date: format(start), end_date: format(end) };
  };

  const normalizeRange = (r) => {
    if (!r) return defaultRange();
    if (r.start_date && r.end_date) return r;
    if (r.fromDate && r.toDate) return { start_date: r.fromDate, end_date: r.toDate };
    return defaultRange();
  };

  const fetchData = useCallback(
    async (attempt = 0) => {
      if (!mountedRef.current) return;

      setIsLoading(true);
      setError("");
      setMessage("");

      try {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }

        if (!isConnected) {
          return;
        }

        const appliedRange = normalizeRange(range);

        const payload = {
          group_id: groupId,
          role_id: roleId,
          start_date: appliedRange.start_date,
          end_date: appliedRange.end_date,
        };

        if (stationIds) payload.station_ids = stationIds;

        console.log("Daywise Station Group request:", payload);

        const response = await send("cms_get_daywise_by_stationwise_stat_group", payload);

        console.log("Daywise Station Group response:", response);

        if (!response || response.type !== "cms_get_daywise_by_stationwise_stat_group_response") {
          throw new Error("Invalid response from server");
        }

        if (!Array.isArray(response.data)) {
          throw new Error("Server did not return a valid data list");
        }

        setData(response.data);
        setMessage(response.message || "");
        setError(""); // clear previous error
      } catch (err) {
        console.error("Fetch error:", err.message);
        setError(err.message);
        if (mountedRef.current) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
          retryTimeoutRef.current = setTimeout(() => fetchData(attempt + 1), delay);
        }
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [isConnected, connect, send, groupId, roleId, range, stationIds]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    const unregister = registerHandler("daywise_by_stationwise_group_update", (msg) => {
      if (Array.isArray(msg.data) && mountedRef.current) {
        setData(msg.data); // update data when new message arrives
      }
    });

    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      unregister();
    };
  }, [fetchData, registerHandler]);

  return { data, isLoading, error, message, refetch: () => fetchData(0) };
}
